import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useFirebase } from "@components/firebase/useFirebase";
import { useAuth } from "@components/auth/useAuth";
import {
  getMyMessages,
  sendMessage,
  markMessageRead,
  markMessageUnread,
  trashMessage,
  restoreMessage,
} from "@/services/firebase/messaging-functions";
import { getRealtimeWebSocketUrl } from "@/services/firebase/websocket-sync-functions";
import { MessageModel, ISendMessageRequest, MessageType } from "common247";
import { entitySync } from "@/services/sync/entity-sync";

/**
 * Polling interval for refreshing messages (1 minute).
 */
export const MESSAGES_REFRESH_INTERVAL_MS = 60 * 1000;

/**
 * Hook providing real-time messaging state, unread badge count, and message actions.
 * Maintains WebSocket connection to Cloud Functions for real-time delivery and unread notifications.
 */
export function useMessaging(
  options: {
    typeFilter?: MessageType;
    autoFetch?: boolean;
    folder?: "inbox" | "sent" | "trash" | "all";
  } = {}
) {
  const { typeFilter, autoFetch = true, folder = "all" } = options;
  const services = useFirebase();
  const { user, isAuthenticated } = useAuth();
  const currentUserId = user ? (user.userId || (user as any).uid || "") : "";

  const [messages, setMessages] = useState<MessageModel[]>([]);
  const [serverUnreadCount, setServerUnreadCount] = useState<number>(0);
  const [hasLoaded, setHasLoaded] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isConnected, setIsConnected] = useState<boolean>(false);

  const socketRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<any>(null);

  const currentUids = useMemo(() => {
    if (!user) return [];
    return [(user as any).uid, user.userId, (user as any).authUid].filter(Boolean) as string[];
  }, [user]);

  /**
   * Helper to determine if a message is trashed by the current user.
   */
  const isTrashedByMe = useCallback(
    (m: MessageModel): boolean => {
      if (m.isTrashed || (m as any).trashed) return true;
      if (!user) return false;
      if (Array.isArray(m.recipients)) {
        const myRec = m.recipients.find((r) => currentUids.includes(r.contactId));
        if (myRec && myRec.trashed !== undefined) {
          return myRec.trashed;
        }
      }
      return false;
    },
    [user, currentUids]
  );

  /**
   * Helper to determine if current user is a recipient of a message.
   */
  const isRecipientOf = useCallback(
    (m: MessageModel): boolean => {
      if (!user) return false;
      if (typeof m.isRecipient === "boolean") return m.isRecipient;
      if (Array.isArray(m.recipients) && m.recipients.length > 0) {
        return m.recipients.some(
          (r) =>
            currentUids.includes(r.contactId) ||
            Boolean(r.groupId) ||
            (user.email && r.contactId?.toLowerCase() === user.email.toLowerCase())
        );
      }
      if ((m as any).recipientUserId && currentUids.includes((m as any).recipientUserId)) return true;
      if ((m as any).recipientId && currentUids.includes((m as any).recipientId)) return true;
      if ((m as any).targetType === "group" || Boolean((m as any).groupId)) return true;
      return false;
    },
    [user, currentUids]
  );

  /**
   * Helper to determine if current user sent the message.
   */
  const isSentByMe = useCallback(
    (m: MessageModel): boolean => {
      if (!user) return false;
      if (typeof m.isSent === "boolean") return m.isSent;
      if (m.senderUserId && currentUids.includes(m.senderUserId)) return true;
      if (m.senderContactId && currentUids.includes(m.senderContactId)) return true;
      if (user.email && m.senderEmail && m.senderEmail.toLowerCase() === user.email.toLowerCase()) {
        return true;
      }
      return false;
    },
    [user, currentUids]
  );

  /**
   * Helper to determine if a message belongs to the Inbox folder.
   * A message is in Inbox if it is not trashed, and is either incoming or self-sent where user is recipient.
   */
  const isInboxMessage = useCallback(
    (m: MessageModel): boolean => {
      if (isTrashedByMe(m)) return false;
      return !isSentByMe(m) || isRecipientOf(m);
    },
    [isTrashedByMe, isSentByMe, isRecipientOf]
  );

  /**
   * Dynamically derived count of unread messages strictly residing in the Inbox folder.
   */
  const inboxUnreadCount = useMemo(() => {
    return messages.filter((m) => isInboxMessage(m) && !m.isRead).length;
  }, [messages, isInboxMessage]);

  const unreadCount = useMemo(() => {
    if (folder === "sent" || folder === "trash") {
      return serverUnreadCount;
    }
    return hasLoaded ? inboxUnreadCount : serverUnreadCount;
  }, [folder, hasLoaded, inboxUnreadCount, serverUnreadCount]);

  /**
   * Fetches the latest messages and unread count from the backend.
   */
  const refreshMessages = useCallback(async () => {
    if (!services || !isAuthenticated || !user) return;
    setIsLoading(true);
    try {
      const res = await getMyMessages(services, {
        type: typeFilter,
        folder,
      });
      if (res && res.status) {
        setMessages(res.messages || []);
        setServerUnreadCount(res.unreadCount || 0);
        setHasLoaded(true);
      }
    } catch (err) {
      console.warn("Error fetching user messages:", err);
    } finally {
      setIsLoading(false);
    }
  }, [services, isAuthenticated, user, typeFilter, folder]);

  /**
   * Sends a new direct or group message.
   */
  const sendNewMessage = useCallback(
    async (request: ISendMessageRequest): Promise<MessageModel | null> => {
      if (!services) throw new Error("Firebase services not initialized.");
      const res = await sendMessage(services, request);
      if (res && res.status && res.sentMessage) {
        const msgWithIsRead: MessageModel = {
          ...res.sentMessage,
          isRead: true,
          isSent: true,
        };
        setMessages((prev) => [msgWithIsRead, ...prev]);
        return msgWithIsRead;
      }
      throw new Error(res.error || "Failed to send message.");
    },
    [services]
  );

  /**
   * Marks a message as read.
   */
  const markAsRead = useCallback(
    async (messageId: string) => {
      if (!services || !user) return;
      try {
        const target = messages.find((m) => m.id === messageId);
        const res = await markMessageRead(services, { messageId });
        if (!res?.status) {
          console.warn("Failed to mark message read on backend:", res?.error);
          return;
        }
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === messageId
              ? {
                  ...msg,
                  isRead: true,
                  recipients: msg.recipients?.map((r) =>
                    currentUids.includes(r.contactId)
                      ? { ...r, read: true, readAt: Date.now() }
                      : r
                  ),
                }
              : msg
          )
        );
        if (target && isInboxMessage(target) && !target.isRead) {
          setServerUnreadCount((prev) => Math.max(0, prev - 1));
        }
      } catch (err) {
        console.warn("Failed to mark message read:", err);
      }
    },
    [services, user, messages, isInboxMessage, currentUids]
  );

  /**
   * Marks a message as unread.
   */
  const markAsUnread = useCallback(
    async (messageId: string) => {
      if (!services || !user) return;
      try {
        const target = messages.find((m) => m.id === messageId);
        const res = await markMessageUnread(services, { messageId });
        if (!res?.status) {
          console.warn("Failed to mark message unread on backend:", res?.error);
          return;
        }
        setMessages((prev) =>
          prev.map((msg) => {
            if (msg.id !== messageId) return msg;
            return {
              ...msg,
              isRead: false,
              recipients: msg.recipients?.map((r) =>
                currentUids.includes(r.contactId) ? { ...r, read: false } : r
              ),
            };
          })
        );
        if (target && isInboxMessage(target) && target.isRead) {
          setServerUnreadCount((prev) => prev + 1);
        }
      } catch (err) {
        console.warn("Failed to mark message unread:", err);
      }
    },
    [services, user, messages, isInboxMessage, currentUids]
  );

  /**
   * Moves a message to the user's trash folder.
   */
  const moveToTrash = useCallback(
    async (messageId: string) => {
      if (!services || !user) return;
      try {
        const target = messages.find((m) => m.id === messageId);
        const res = await trashMessage(services, { messageId });
        if (!res?.status) {
          console.warn("Failed to move message to trash on backend:", res?.error);
          return;
        }
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === messageId
              ? {
                  ...msg,
                  isTrashed: true,
                  recipients: msg.recipients?.map((r) =>
                    currentUids.includes(r.contactId)
                      ? { ...r, trashed: true, trashedAt: Date.now() }
                      : r
                  ),
                }
              : msg
          )
        );
        // If message was an unread inbox message, decrement server unread count
        if (target && isInboxMessage(target) && !target.isRead) {
          setServerUnreadCount((prev) => Math.max(0, prev - 1));
        }
      } catch (err) {
        console.warn("Failed to move message to trash:", err);
      }
    },
    [services, user, messages, isInboxMessage, currentUids]
  );

  /**
   * Restores a message from the user's trash folder.
   */
  const restoreFromTrash = useCallback(
    async (messageId: string) => {
      if (!services || !user) return;
      try {
        const target = messages.find((m) => m.id === messageId);
        const res = await restoreMessage(services, { messageId });
        if (!res?.status) {
          console.warn("Failed to restore message from trash on backend:", res?.error);
          return;
        }
        setMessages((prev) =>
          prev.map((msg) => {
            if (msg.id !== messageId) return msg;
            return {
              ...msg,
              isTrashed: false,
              recipients: msg.recipients?.map((r) =>
                currentUids.includes(r.contactId) ? { ...r, trashed: false } : r
              ),
            };
          })
        );
        // If restored message belongs to inbox and was unread, increment server unread count
        if (target && (!isSentByMe(target) || isRecipientOf(target)) && !target.isRead) {
          setServerUnreadCount((prev) => prev + 1);
        }
      } catch (err) {
        console.warn("Failed to restore message from trash:", err);
      }
    },
    [services, user, messages, isSentByMe, isRecipientOf, currentUids]
  );

  // Initial fetch
  useEffect(() => {
    if (autoFetch && isAuthenticated && user) {
      refreshMessages();
    }
  }, [autoFetch, isAuthenticated, user, refreshMessages]);

  // Connect WebSocket for real-time delivery
  useEffect(() => {
    if (!isAuthenticated || !user) {
      if (socketRef.current) {
        socketRef.current.close();
        socketRef.current = null;
      }
      setIsConnected(false);
      return;
    }

    let isDisposed = false;
    let retryCount = 0;
    const maxRetries = 3;
    let fallbackPollTimer: any = null;

    const startFallbackPolling = () => {
      if (fallbackPollTimer || isDisposed) return;
      setIsConnected(true);
      fallbackPollTimer = setInterval(() => {
        if (!isDisposed) {
          refreshMessages();
        }
      }, MESSAGES_REFRESH_INTERVAL_MS);
    };

    const wsUrl = getRealtimeWebSocketUrl();

    // If WebSocket is not supported in the current environment (e.g. local emulator), use polling mode directly
    if (!wsUrl) {
      startFallbackPolling();
      return () => {
        isDisposed = true;
        if (fallbackPollTimer) {
          clearInterval(fallbackPollTimer);
          fallbackPollTimer = null;
        }
        setIsConnected(false);
      };
    }

    const connectWebSocket = () => {
      if (isDisposed) return;
      try {
        const ws = new WebSocket(wsUrl);
        socketRef.current = ws;

        ws.onopen = () => {
          if (isDisposed) {
            ws.close();
            return;
          }
          retryCount = 0;
          setIsConnected(true);
          if (fallbackPollTimer) {
            clearInterval(fallbackPollTimer);
            fallbackPollTimer = null;
          }

          const isAdmin =
            user.claims?.admin === true ||
            user.claims?.admin === "*" ||
            (Array.isArray(user.claims?.admin) && user.claims.admin.length > 0) ||
            (typeof user.claims?.admin === "string" && user.claims.admin.length > 0);

          const groupIds: string[] = [];
          if (isAdmin) {
            groupIds.push("system-admins");
          }

          // Obtain idToken for authenticated subscription if available
          let idToken: string | undefined = undefined;
          if (user && typeof (user as any).getIdToken === "function") {
            try {
              idToken = (user as any).getIdToken();
            } catch (e) {
              // Non-blocking fallback
            }
          }

          // Subscribe with user UID, idToken, and group IDs
          ws.send(
            JSON.stringify({
              action: "subscribe",
              userUid: currentUserId,
              idToken,
              groupIds,
            })
          );

          // Connect EntitySyncService to WebSocket transport
          entitySync.setTransportSender((msg) => {
            if (ws.readyState === WebSocket.OPEN) {
              ws.send(JSON.stringify(msg));
            }
          });
        };

        ws.onmessage = (event) => {
          if (isDisposed) return;
          try {
            const data = JSON.parse(event.data);
            if (data.event === "new_message" && data.data) {
              const incoming: MessageModel = data.data;
              const isSentByMe = Boolean(
                user && (
                  (typeof incoming.isSent === "boolean" && incoming.isSent) ||
                  (incoming.senderUserId && (incoming.senderUserId === currentUserId || incoming.senderUserId === (user.userId || (user as any).uid))) ||
                  (incoming.senderContactId && (incoming.senderContactId === currentUserId || incoming.senderContactId === (user.userId || (user as any).uid))) ||
                  (user.email && incoming.senderEmail && incoming.senderEmail.toLowerCase() === user.email.toLowerCase())
                )
              );
              let isRead = false;
              if (isSentByMe) {
                if (Array.isArray(incoming.recipients) && incoming.recipients.length > 0) {
                  isRead = incoming.recipients.some((r) => r.read);
                }
              } else {
                if (Array.isArray(incoming.recipients) && incoming.recipients.length > 0) {
                  const myRec = incoming.recipients.find((r) => r.contactId === currentUserId || ((user as any).uid && r.contactId === (user as any).uid));
                  isRead = myRec ? myRec.read : false;
                }
              }

              const isRecipient = Boolean(
                user && (
                  (typeof incoming.isRecipient === "boolean" && incoming.isRecipient) ||
                  (Array.isArray(incoming.recipients) &&
                    incoming.recipients.some(
                      (r) =>
                        r.contactId === currentUserId ||
                        ((user as any).uid && r.contactId === (user as any).uid) ||
                        (user.userId && r.contactId === user.userId) ||
                        Boolean(r.groupId) ||
                        (user.email && r.contactId?.toLowerCase() === user.email.toLowerCase())
                    )) ||
                  (incoming as any).targetType === "group" ||
                  Boolean((incoming as any).groupId)
                )
              );

              setMessages((prev) => {
                // If message already in list, do not duplicate
                if (prev.some((m) => m.id === incoming.id)) {
                  return prev;
                }
                return [
                  {
                    ...incoming,
                    isRead,
                    isSent: isSentByMe,
                    isRecipient,
                  },
                  ...prev,
                ];
              });

              if ((!isSentByMe || isRecipient) && !isRead) {
                setServerUnreadCount((prev) => prev + 1);
              }
            } else if (data.event === "message_read" && data.data) {
              const { messageId, userUid } = data.data;
              let wasUnreadInbox = false;
              setMessages((prev) =>
                prev.map((m) => {
                  if (m.id !== messageId) return m;
                  const updatedRecipients = Array.isArray(m.recipients)
                    ? m.recipients.map((r) => (r.contactId === userUid ? { ...r, read: true, readAt: Date.now() } : r))
                    : m.recipients;
                  const isSentByMe = Boolean(
                    user && (
                      (typeof m.isSent === "boolean" && m.isSent) ||
                      (m.senderUserId && (m.senderUserId === currentUserId || m.senderUserId === (user.userId || (user as any).uid))) ||
                      (m.senderContactId && (m.senderContactId === currentUserId || m.senderContactId === (user.userId || (user as any).uid))) ||
                      (user.email && m.senderEmail && m.senderEmail.toLowerCase() === user.email.toLowerCase())
                    )
                  );
                  let isRead = false;
                  if (isSentByMe) {
                    isRead = updatedRecipients && updatedRecipients.length > 0
                      ? updatedRecipients.some((r) => r.read)
                      : false;
                  } else {
                    const myRec = updatedRecipients?.find((r) => r.contactId === currentUserId || ((user as any).uid && r.contactId === (user as any).uid));
                    isRead = myRec ? myRec.read : false;
                  }
                  if (!m.isTrashed && (!isSentByMe || m.isRecipient) && !m.isRead) {
                    wasUnreadInbox = true;
                  }
                  return { ...m, recipients: updatedRecipients, isRead };
                })
              );
              if (wasUnreadInbox && (userUid === currentUserId || userUid === (user as any).uid)) {
                setServerUnreadCount((prev) => Math.max(0, prev - 1));
              }
            } else if (data.event === "message_unread" && data.data) {
              const { messageId, userUid } = data.data;
              let isTargetInbox = false;
              setMessages((prev) =>
                prev.map((m) => {
                  if (m.id !== messageId) return m;
                  const updatedRecipients = Array.isArray(m.recipients)
                    ? m.recipients.map((r) => (r.contactId === userUid ? { ...r, read: false } : r))
                    : m.recipients;
                  const isSentByMe = Boolean(
                    user && (
                      (typeof m.isSent === "boolean" && m.isSent) ||
                      (m.senderUserId && (m.senderUserId === currentUserId || m.senderUserId === (user.userId || (user as any).uid))) ||
                      (m.senderContactId && (m.senderContactId === currentUserId || m.senderContactId === (user.userId || (user as any).uid))) ||
                      (user.email && m.senderEmail && m.senderEmail.toLowerCase() === user.email.toLowerCase())
                    )
                  );
                  let isRead = false;
                  if (isSentByMe) {
                    isRead = updatedRecipients && updatedRecipients.length > 0
                      ? updatedRecipients.some((r) => r.read)
                      : false;
                  } else {
                    const myRec = updatedRecipients?.find((r) => r.contactId === currentUserId || ((user as any).uid && r.contactId === (user as any).uid));
                    isRead = myRec ? myRec.read : false;
                  }
                  if (!m.isTrashed && (!isSentByMe || m.isRecipient)) {
                    isTargetInbox = true;
                  }
                  return { ...m, recipients: updatedRecipients, isRead };
                })
              );
              if (isTargetInbox && (userUid === currentUserId || userUid === (user as any).uid)) {
                setServerUnreadCount((prev) => prev + 1);
              }
            } else if (data.event === "entity_change" && data.data) {
              entitySync.handleRemoteEntityChange(data.data, data.topics);
            }
          } catch (e) {
            console.warn("Error parsing WebSocket messaging frame:", e);
          }
        };

        ws.onclose = () => {
          entitySync.setTransportSender(null);
          if (isDisposed) return;
          setIsConnected(false);
          startFallbackPolling();
          if (retryCount < maxRetries) {
            retryCount++;
            const delay = Math.min(30000, 5000 * Math.pow(2, retryCount - 1));
            reconnectTimeoutRef.current = setTimeout(connectWebSocket, delay);
          }
        };

        ws.onerror = (err) => {
          console.warn("Messaging WebSocket error:", err);
          ws.close();
        };
      } catch (err) {
        console.warn("Could not initiate messaging WebSocket:", err);
        startFallbackPolling();
        if (retryCount < maxRetries) {
          retryCount++;
          reconnectTimeoutRef.current = setTimeout(connectWebSocket, 10000);
        }
      }
    };

    connectWebSocket();

    return () => {
      isDisposed = true;
      entitySync.setTransportSender(null);
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (fallbackPollTimer) {
        clearInterval(fallbackPollTimer);
        fallbackPollTimer = null;
      }
      if (socketRef.current) {
        socketRef.current.close();
        socketRef.current = null;
      }
      setIsConnected(false);
    };
  }, [isAuthenticated, user, refreshMessages]);

  return {
    messages,
    unreadCount,
    isLoading,
    isConnected,
    refreshMessages,
    sendNewMessage,
    markAsRead,
    markAsUnread,
    moveToTrash,
    restoreFromTrash,
  };
}
